import { useSelectedOrganisation } from "xplatform_shell/useSelectedOrganisation";
import { AdminPermissions } from "@config/permissions";
import { UserRole } from "@models/common";

export const useAdminPermissions = () => {
  const { organisation } = useSelectedOrganisation();

  const effectivePermissions = organisation?.effectivePermissions ?? [];

  // Deny all when organisation is not yet loaded.
  // Do NOT fall back to the shell's hasPermission, which may default to true.
  const hasPermission = (permission: string): boolean =>
    organisation != null && effectivePermissions.includes(permission);

  return {
    effectivePermissions,
    hasPermission,

    // Roles
    isSuperAdmin: organisation?.roles.includes(UserRole.SuperAdmin) === true || organisation?.roles.includes(UserRole.OrganizationOwner) === true,

    isOrgLeader: organisation?.roles.includes(UserRole.OrgLeader) === true,

    // Cross-organisation access — gates creating doctypes + configuring flows.
    isCrossOrg: hasPermission(AdminPermissions.CROSS_ORG),

    // Dashboard
    canViewDashboard: hasPermission(AdminPermissions.DASHBOARD_VIEW),

    // Doctype
    canViewDoctype:
      hasPermission(AdminPermissions.DOCTYPE_VIEW) ||
      hasPermission(AdminPermissions.DOCTYPE_MANAGE),
    // Can edit existing doctypes (name, description, status, category, flow config).
    // Model/provider selection is read-only in edit mode.
    canEditDoctype: hasPermission(AdminPermissions.DOCTYPE_MANAGE),

    // Can create NEW doctypes — requires provider visibility to select model.
    canCreateDoctype:
      hasPermission(AdminPermissions.DOCTYPE_MANAGE) &&
      hasPermission(AdminPermissions.PROVIDER_MODEL_VIEW) &&
      hasPermission(AdminPermissions.PROVIDER_VIEW),


    // Category
    canViewCategory:
      hasPermission(AdminPermissions.CATEGORY_VIEW) ||
      hasPermission(AdminPermissions.CATEGORY_MANAGE),
    canManageCategory: hasPermission(AdminPermissions.CATEGORY_MANAGE),

    // Evaluation
    canViewEvaluation: hasPermission(AdminPermissions.EVALUATION_VIEW),
    canTriggerEvaluation: hasPermission(AdminPermissions.EVALUATION_TRIGGER),

    // Monitoring
    canViewMonitoring: hasPermission(AdminPermissions.MONITORING_VIEW),

    // Provider
    canViewProvider: hasPermission(AdminPermissions.PROVIDER_VIEW),
    canViewProviderCredential:
      hasPermission(AdminPermissions.PROVIDER_CREDENTIAL_VIEW) ||
      hasPermission(AdminPermissions.PROVIDER_CREDENTIAL_MANAGE),
    canManageProviderCredential: hasPermission(
      AdminPermissions.PROVIDER_CREDENTIAL_MANAGE,
    ),
    canViewProviderModel:
      hasPermission(AdminPermissions.PROVIDER_MODEL_VIEW) ||
      hasPermission(AdminPermissions.PROVIDER_MODEL_MANAGE),
    canManageProviderModel: hasPermission(
      AdminPermissions.PROVIDER_MODEL_MANAGE,
    ),
  };
};
